import { useState } from 'react'
import { Copy, Download, Eye, FolderInput, Mail, Pencil, Send, Trash2 } from 'lucide-react'
import { downloadListCsv } from '@/features/lists/listExport'
import ListFormModal from '@/features/lists/ListFormModal'
import {
  AddToSequenceModal,
  DeleteListModal,
  DuplicateListModal,
  MoveListModal,
  SendToListModal,
} from '@/features/lists/ListActionModals'
import type { ActionItem } from '@/components/ui/ActionMenu'
import type { ContactList } from '@/api/lists'

type Kind = 'edit' | 'duplicate' | 'move' | 'delete' | 'sequence' | 'send'

/**
 * Everything you can do to a list, shared by the lists table (row ⋯ menu) and the list page: the menu
 * items, and the dialog for whichever action is open.
 */
export function useListActions({
  onView,
  onDeleted,
  onDuplicated,
}: {
  onView?: (list: ContactList) => void
  onDeleted?: () => void
  onDuplicated?: (copy: ContactList) => void
} = {}) {
  const [open, setOpen] = useState<{ kind: Kind; list: ContactList } | null>(null)
  const start = (kind: Kind, list: ContactList) => () => setOpen({ kind, list })
  const close = () => setOpen(null)

  function itemsFor(list: ContactList, { includeView = true } = {}): ActionItem[] {
    const items: ActionItem[] = []
    if (includeView && onView) items.push({ label: 'View list', icon: <Eye size={15} />, onClick: () => onView(list) })
    items.push(
      { label: 'Edit list', icon: <Pencil size={15} />, onClick: start('edit', list) },
      { label: 'Send email or SMS', icon: <Send size={15} />, onClick: start('send', list) },
      { label: 'Add to sequence', icon: <Mail size={15} />, onClick: start('sequence', list) },
      { label: 'Duplicate', icon: <Copy size={15} />, onClick: start('duplicate', list) },
      { label: 'Move to folder', icon: <FolderInput size={15} />, onClick: start('move', list) },
      { label: 'Export contacts', icon: <Download size={15} />, onClick: () => void downloadListCsv(list) },
      { label: 'Delete list', icon: <Trash2 size={15} />, onClick: start('delete', list), danger: true },
    )
    return items
  }

  let dialog = null
  if (open) {
    const { kind, list } = open
    if (kind === 'edit') dialog = <ListFormModal list={list} onClose={close} />
    if (kind === 'duplicate') dialog = <DuplicateListModal list={list} onClose={close} onDone={onDuplicated} />
    if (kind === 'move') dialog = <MoveListModal list={list} onClose={close} />
    if (kind === 'delete') dialog = <DeleteListModal list={list} onClose={close} onDeleted={onDeleted} />
    if (kind === 'sequence') dialog = <AddToSequenceModal list={list} onClose={close} />
    if (kind === 'send') dialog = <SendToListModal list={list} onClose={close} />
  }

  return { itemsFor, dialog, openAction: (kind: Kind, list: ContactList) => setOpen({ kind, list }) }
}
